import React from 'react';
import { useToast } from '../components/notifications/ToastProvider';

export interface NotificationAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  icon?: React.ReactNode;
}

type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-center'
  | 'bottom-center'
  | 'center';

/**
 * Hook personnalisé pour remplacer tous les alert() par des notifications toast
 * Utilisation : const notify = useNotifications();
 * notify.success('Titre', 'Message optionnel');
 * notify.error('Erreur', 'Détails de l\'erreur');
 */
export const useNotifications = () => {
  const toast = useToast();
  
  // Vérification de sécurité : si le contexte n'est pas disponible, utiliser ALERT en attendant
  if (!toast) {
    console.error('⚠️ useNotifications: ToastContext non trouvé. Assurez-vous que le composant est enveloppé dans ToastProvider.');
    // Fallback TEMPORAIRE: utiliser alert() pour voir si le problème vient du contexte
    return {
      success: (title: string, message?: string) => alert(`✅ ${title}\n${message || ''}`),
      error: (title: string, message?: string) => alert(`❌ ${title}\n${message || ''}`),
      info: (title: string, message?: string) => alert(`ℹ️ ${title}\n${message || ''}`),
      warning: (title: string, message?: string) => alert(`⚠️ ${title}\n${message || ''}`),
      leadCreated: () => alert('Lead created'),
      clientSaved: () => alert('Client saved'),
      appointmentScheduled: () => alert('Appointment scheduled'),
    };
  }
  
  console.log('✅ useNotifications: ToastContext trouvé et fonctionnel !');
  
  const { showSuccess, showError, showInfo, showWarning, showLeadCreated, showToast } = toast as any;

  // Remplace alert() pour les succès (supporte durée et position)
  const success = (
    title: string,
    message?: string,
    actions?: NotificationAction[],
    duration: number = 5000,
    position?: ToastPosition
  ) => {
    return showSuccess(title, message, actions, duration, position);
  };

  // Remplace alert() pour les erreurs
  const error = (title: string, message?: string, actions?: NotificationAction[]) => {
    return showError(title, message, actions);
  };

  // Remplace alert() pour les informations
  const info = (title: string, message?: string, actions?: NotificationAction[]) => {
    return showInfo(title, message, actions);
  };

  // Remplace alert() pour les avertissements
  const warning = (
    title: string,
    message?: string,
    actions?: NotificationAction[],
    duration: number = 5000,
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
  ) => {
    // Fallback compatible: si showWarning ne supporte pas duration, utiliser showToast
    try {
      return showWarning(title, message, actions, duration, position);
    } catch {
      return showToast({ title, message, type: 'warning', actions, duration, position });
    }
  };

  // Notification spécialisée pour les leads
  const leadCreated = (leadId: string, leadName: string, navigateToLead?: (id: string) => void, openTaskModal?: (id: string) => void) => {
    return showLeadCreated(
      leadId, 
      leadName, 
      navigateToLead ? () => navigateToLead(leadId) : undefined, 
      openTaskModal ? () => openTaskModal(leadId) : undefined
    );
  };

  // Méthodes de convenance pour les cas fréquents de votre CRM
  const clientSaved = (clientName: string, clientNumber: string) => {
    return success(
      'Client enregistré !',
      `Le client "${clientName}" a été ajouté avec le numéro de dossier: ${clientNumber}`
    );
  };

  const clientUpdated = (clientName: string) => {
    return success(
      'Client modifié !',
      `Les informations de "${clientName}" ont été mises à jour avec succès.`
    );
  };

  const documentUploaded = (documentType: string) => {
    return success(
      'Document ajouté !',
      `Le document "${documentType}" a été enregistré avec succès.`
    );
  };

  const authError = () => {
    return error(
      'Erreur d\'authentification',
      'Vous devez être connecté pour effectuer cette action. Veuillez vous reconnecter.'
    );
  };

  const serverError = () => {
    return error(
      'Erreur de connexion',
      'Impossible de communiquer avec le serveur. Vérifiez que le backend est démarré.'
    );
  };

  const validationError = (fieldName: string) => {
    return warning(
      'Champ requis',
      `Veuillez remplir le champ "${fieldName}" avant de continuer.`
    );
  };

  const formErrors = () => {
    return warning(
      'Formulaire incomplet',
      'Veuillez corriger les erreurs dans le formulaire avant de soumettre.'
    );
  };

  return {
    success,
    error,
    info,
    warning,
    leadCreated,
    clientSaved,
    clientUpdated,
    documentUploaded,
    authError,
    serverError,
    validationError,
    formErrors
  };
};

export default useNotifications;